/**
 * The profile avatar this session just saved, by its S3 upload key (#885 D6).
 *
 * Rule 1 (the uploader sees their picture the instant they pick it) without a
 * client-written `ready`: the server's FileRef stays the source of truth, and
 * the header shows the pick through the local-echo layer. `uploadFileToS3`
 * registers the picked bytes under the upload key; ProfileEditDialog records
 * that key here once the profile PUT accepted it; the header resolves
 * `resolveFileRef(user.avatar, key)`. The precedence is the file layer's
 * (§14.3.6): a ready rendition beats the echo, a `failed` file is never
 * painted with the echo (the header falls back to initials, #885 D4), and a
 * `processing` file shows the echo.
 *
 * Client state only (Zustand): a key, and the user it was saved for, so a
 * session that changes hands never paints one person's pick on another's
 * header. Cleared on logout (store registry), on avatar removal, and when the
 * server reports the file terminal.
 */
import { create } from 'zustand';
import { registerStoreReset } from '@/store/registry';
import { useFileRef, type FileRefView } from '@/features/files';
import type { User } from '@/types';

interface PendingAvatarState {
  userId: number | null;
  key: string | null;
  record: (userId: number, key: string) => void;
  clear: () => void;
}

export const usePendingAvatarStore = create<PendingAvatarState>()((set) => ({
  userId: null,
  key: null,
  record: (userId, key) => set({ userId, key }),
  clear: () => set({ userId: null, key: null }),
}));

registerStoreReset(() => usePendingAvatarStore.setState({ userId: null, key: null }), { onUserSwitch: true });

/** The pending upload key for `user`, or null when none was saved for them. */
export function usePendingAvatarKey(user: Pick<User, 'id'> | null | undefined): string | null {
  return usePendingAvatarStore((s) => (user && s.userId === user.id ? s.key : null));
}

/**
 * The current user's avatar as the header shows it: the server FileRef,
 * resolved with this session's pending pick as its local echo.
 */
export function useUserAvatarView(user: Pick<User, 'id' | 'avatar'> | null | undefined): FileRefView {
  const pendingKey = usePendingAvatarKey(user);
  return useFileRef(user?.avatar, { localKey: pendingKey });
}
